// 回放時間軸互動的純邏輯（SPEC #vms-web-timeline PLAY-01、驗收 W21）。設計規則與 App 共用（#device-app-s3-1 APLAY）：
// 共用案例在 docs/testdata/kinio-timeline.json（從 app/home-base 同路徑複製，兩邊要一致；timeline.test.ts 讀它）。
//
// 模型：播放頭固定在中線；拖曳 = 捲動時間（內容往左拖 = 中線時間變晚）；中線夾在 [當天 00:00, min(當天結束, 現在)]，不能捲進未來。
// 放開 300 ms 內沒再拖才從中線那一刻播放；拖曳中不 seek。縮放六級（24 小時 → 1 分鐘），以游標為錨點。
// 「只看有事件的時段」：沒事件的時段折成一條細縫——時間軸改用「虛擬秒」（Axis），折掉的時段長度 0。
// 時間單位：秒（unix）；畫面單位 px。

// ---- 常數（照共用案例） ----

/** App 的固定可視範圍；VMS 會縮放，這只當拖曳 / 合併計算的預設值。 */
export const TIMELINE_VISIBLE_SPAN_S = 3600;
export const TIMELINE_SEEK_DEBOUNCE_MS = 300;
export const TIMELINE_EVENT_PREROLL_S = 3;
export const TIMELINE_MARKER_PX = 18;
/** 縮圖：滑過停 250 ms 才問；拖曳中最多每 300 ms 問一次。 */
export const THUMB_HOVER_DELAY_MS = 250;
export const THUMB_DRAG_INTERVAL_MS = 300;
/** 慣性最多再捲幾個可視寬度。 */
export const INERTIA_MAX_SPANS = 1.5;

// ---- 事件群組（顏色 / 圖示；App 三平台與 VMS 同一組，SPEC PLAY-01「事件標記」） ----

export type EventGroup = 'person' | 'vehicle' | 'animal' | 'package' | 'sound' | 'sensor' | 'alarm' | 'motion';

export const EVENT_GROUPS: readonly EventGroup[] = ['alarm', 'person', 'vehicle', 'animal', 'package', 'sound', 'sensor', 'motion'];

export const EVENT_GROUP_STYLE: Record<EventGroup, { color: string; icon: string }> = {
  person: { color: '#2F80ED', icon: '🧍' },
  vehicle: { color: '#8E44AD', icon: '🚗' },
  animal: { color: '#27AE60', icon: '🐾' },
  package: { color: '#E67E22', icon: '📦' },
  sound: { color: '#16A2B8', icon: '🔊' },
  sensor: { color: '#7F8C8D', icon: '📟' },
  alarm: { color: '#E74C3C', icon: '⚠️' },
  motion: { color: '#D4A017', icon: '🏃' },
};

/** 事件目錄 class（SPEC #events-69）→ 群組。 */
export const CLASS_MAP: Record<string, EventGroup> = {
  motion: 'motion',
  person: 'person',
  vehicle: 'vehicle',
  pet: 'animal',
  package: 'package',
  sound: 'sound',
  doorbell: 'person',
  call: 'person',
  tamper: 'alarm',
  alarm: 'alarm',
  contact: 'sensor',
  occupancy: 'sensor',
};

const OBJECT_TYPE_MAP: Record<string, EventGroup> = { person: 'person', vehicle: 'vehicle', pet: 'animal', package: 'package' };

/** 事件 → 時間軸群組；有 `class` 就用它，沒有就看 key（`type`）。沒對到群組（上下線、OTA…）→ null，不上時間軸。 */
export function eventGroup(ev: { class?: unknown; type?: unknown; payload?: unknown }): EventGroup | null {
  if (typeof ev.class === 'string' && ev.class) return CLASS_MAP[ev.class] ?? null;
  const type = typeof ev.type === 'string' ? ev.type : '';
  const cluster = type.split('.')[0];
  if (type === 'kinio_object_recognition.object_detected') {
    const p = ev.payload && typeof ev.payload === 'object' ? (ev.payload as Record<string, unknown>) : {};
    const ot = typeof p.object_type === 'string' ? p.object_type : '';
    return OBJECT_TYPE_MAP[ot] ?? 'motion';
  }
  switch (cluster) {
    case 'kinio_sound_detection':
    case 'kinio_sound_recognition':
      return 'sound';
    case 'kinio_tamper':
    case 'kinio_sensor_alert':
      return 'alarm';
    case 'boolean_state':
    case 'occupancy_sensing':
      return 'sensor';
    case 'kinio_doorbell':
    case 'kinio_call':
      return 'person';
    case 'kinio_motion':
      return 'motion';
    default:
      return null;
  }
}

// ---- 拖曳 ----

export interface TimelineBounds {
  dayStart: number;
  dayEnd: number;
  now: number;
}

/** 中線時間能到的範圍：[dayStart, min(dayEnd, now)]。 */
export function clampCenter(t: number, b: TimelineBounds): number {
  const hi = Math.max(b.dayStart, Math.min(b.dayEnd, b.now));
  return Math.min(hi, Math.max(b.dayStart, t));
}

/** 拖曳：gesture 內 dx 是相對按下點的累計位移；center = clamp(centerAtDown - dx * s_per_px)。 */
export function dragCenter(o: TimelineBounds & { width: number; centerAtDown: number; dx: number; spanS?: number }): number {
  const sPerPx = (o.spanS ?? TIMELINE_VISIBLE_SPAN_S) / Math.max(1, o.width);
  return clampCenter(o.centerAtDown - o.dx * sPerPx, o);
}

export type ScrubberBounds = TimelineBounds & { width: number; spanS?: number };

/**
 * 拖曳 → seek 的時機。down / move / up 帶 ms 時間；`up` 之後 300 ms 內沒再 down 才 seek（`due(now)` 取出）。
 * 呼叫端：拖曳中照 `center` 畫，放開後睡到 `pendingAt` 再 `due()`。鍵盤 / 慣性結束用 `jump` / `settle`。
 */
export class TimelineScrubber {
  center: number;
  private downCenter: number | null = null;
  private pending: { at: number; center: number } | null = null;

  constructor(
    center: number,
    private bounds: ScrubberBounds,
  ) {
    this.center = center;
  }

  get dragging(): boolean {
    return this.downCenter !== null;
  }

  /** 放開後排定的 seek 時間（ms）；沒有 = null。 */
  get pendingAt(): number | null {
    return this.pending?.at ?? null;
  }

  setBounds(b: ScrubberBounds): void {
    this.bounds = b;
    if (!this.dragging) this.center = clampCenter(this.center, b);
  }

  /** 播放中 / 外部改中線（不在拖曳、沒有排定的 seek 時才吃）。 */
  follow(center: number): void {
    if (!this.dragging && !this.pending) this.center = clampCenter(center, this.bounds);
  }

  down(_t: number): void {
    this.pending = null; // 300 ms 內又抓住 → 取消那次 seek
    this.downCenter = this.center;
  }

  move(_t: number, dx: number): number {
    if (this.downCenter === null) return this.center;
    this.center = dragCenter({ ...this.bounds, centerAtDown: this.downCenter, dx });
    return this.center;
  }

  /** 慣性捲動中：直接設中線（仍算拖曳中，不 seek）。 */
  glide(center: number): number {
    if (this.downCenter === null) return this.center;
    this.center = clampCenter(center, this.bounds);
    return this.center;
  }

  up(t: number): void {
    if (this.downCenter === null) return;
    this.downCenter = null;
    this.pending = { at: t + TIMELINE_SEEK_DEBOUNCE_MS, center: this.center };
  }

  /** 鍵盤 / 縮放以外的跳轉：中線移過去，300 ms 內沒再動才 seek（連按只送最後一次）。 */
  jump(t: number, center: number): number {
    this.downCenter = null;
    this.center = clampCenter(center, this.bounds);
    this.pending = { at: t + TIMELINE_SEEK_DEBOUNCE_MS, center: this.center };
    return this.center;
  }

  /** 時間到了就取出要 seek 的中線時間（只取一次）。 */
  due(t: number): number | null {
    if (!this.pending || t < this.pending.at) return null;
    const c = this.pending.center;
    this.pending = null;
    return c;
  }

  /** 不拖了（換日、換攝影機、點標記直接播）：丟掉還沒送的 seek。 */
  reset(center: number): void {
    this.downCenter = null;
    this.pending = null;
    this.center = clampCenter(center, this.bounds);
  }
}

/**
 * 放開時的慣性：最後 80 ms 內的移動算速度（px/ms），沒在動 = 0。位移 = 速度 × 350 ms，最多 1.5 個可視寬度；
 * 回慣性結束時的中線（clamp 過，遇到「現在」停住）。
 */
export function inertiaTarget(
  samples: { t: number; x: number }[],
  upAt: number,
  center: number,
  o: TimelineBounds & { width: number; spanS: number },
): number | null {
  const recent = samples.filter((s) => upAt - s.t <= 80);
  if (recent.length < 2) return null;
  const a = recent[0];
  const b = recent[recent.length - 1];
  if (b.t - a.t < 8) return null;
  const v = (b.x - a.x) / (b.t - a.t);
  if (Math.abs(v) < 0.3) return null;
  const maxPx = INERTIA_MAX_SPANS * o.width;
  const px = Math.max(-maxPx, Math.min(maxPx, v * 350));
  return clampCenter(center - px * (o.spanS / Math.max(1, o.width)), o);
}

// ---- 事件標記 ----

export interface TimelineEventIn {
  start: number;
  group: EventGroup;
  id?: string;
}

export interface TimelineMarker {
  start: number;
  end: number;
  count: number;
  group: EventGroup;
  /** 點了從哪裡播（第一個事件前 3 秒，不早於 dayStart）。 */
  seek: number;
  ids: string[];
}

/** 照開始時間排序，跟前一個事件在畫面上距離 < marker_px 就併進同一群（連鎖）。 */
export function mergeMarkers(events: TimelineEventIn[], o: { width: number; dayStart?: number; spanS?: number; markerPx?: number }): TimelineMarker[] {
  const sPerPx = (o.spanS ?? TIMELINE_VISIBLE_SPAN_S) / Math.max(1, o.width);
  const limitS = (o.markerPx ?? TIMELINE_MARKER_PX) * sPerPx;
  const sorted = [...events].sort((a, b) => a.start - b.start);
  const out: TimelineMarker[] = [];
  let prev: number | null = null;
  for (const e of sorted) {
    const last = out[out.length - 1];
    if (last && prev !== null && e.start - prev < limitS) {
      last.end = e.start;
      last.count += 1;
      if (e.id) last.ids.push(e.id);
    } else {
      out.push({ start: e.start, end: e.start, count: 1, group: e.group, seek: Math.max(o.dayStart ?? 0, e.start - TIMELINE_EVENT_PREROLL_S), ids: e.id ? [e.id] : [] });
    }
    prev = e.start;
  }
  return out;
}

/** Shift + ← / →：上 / 下一個事件的開始（嚴格在 center 之前 / 之後超過 1 秒）；沒有回 null。 */
export function adjacentEvent(starts: number[], center: number, dir: 1 | -1): number | null {
  const sorted = [...starts].sort((a, b) => a - b);
  if (dir > 0) return sorted.find((s) => s > center + 1) ?? null;
  for (let i = sorted.length - 1; i >= 0; i--) if (sorted[i] < center - 1) return sorted[i];
  return null;
}

// ---- 縮放 ----

/** 六級刻度：24 小時、6 小時、1 小時、15 分鐘、5 分鐘、1 分鐘（DST 日 24 小時那級換成當天長度）。 */
export const ZOOM_LEVELS = [86400, 21600, 3600, 900, 300, 60] as const;

export function zoomLevels(dayLen: number): number[] {
  return [dayLen, ...ZOOM_LEVELS.slice(1)];
}

/** 最接近 span 的那一級（對數距離）。 */
export function nearestLevel(span: number, dayLen: number): number {
  const lv = zoomLevels(dayLen);
  let best = lv[0];
  for (const z of lv) if (Math.abs(Math.log(z / span)) < Math.abs(Math.log(best / span))) best = z;
  return best;
}

/** 換一級：dir = +1 放大（範圍變小）、-1 縮小。 */
export function stepLevel(span: number, dayLen: number, dir: 1 | -1): number {
  const lv = zoomLevels(dayLen);
  const i = lv.indexOf(nearestLevel(span, dayLen));
  return lv[Math.min(lv.length - 1, Math.max(0, i + dir))];
}

/**
 * 以錨點縮放：錨點（虛擬秒 anchor，位在畫面 frac 0–1）縮放前後留在同一個位置 → 新的中線（不 clamp，呼叫端夾）。
 */
export function zoomCenter(anchor: number, frac: number, newSpan: number): number {
  return anchor - (frac - 0.5) * newSpan;
}

/** 刻度：minor = 一格（← / → 移動的量）、major = 標籤間距。標籤依級距是小時、10 分、1 分、10 秒。 */
export function tickSteps(span: number): { minor: number; major: number } {
  if (span > 21600) return { minor: 3600, major: 10800 };
  if (span > 3600) return { minor: 900, major: 3600 };
  if (span > 900) return { minor: 300, major: 600 };
  if (span > 300) return { minor: 60, major: 60 };
  if (span > 60) return { minor: 10, major: 60 };
  return { minor: 5, major: 10 };
}

// ---- 軸：真實時間 ↔ 虛擬秒（「只看有事件的時段」把沒事件的時段折成長度 0 的細縫） ----

export interface AxisPiece {
  /** 真實時間 [from, to) */
  from: number;
  to: number;
  /** 這段在虛擬軸的起點 */
  v: number;
}

export interface Axis {
  dayStart: number;
  /** 虛擬軸長度（秒） */
  length: number;
  collapsed: boolean;
  pieces: AxisPiece[];
  /** 折掉的時段：虛擬位置 + 真實範圍（縫上標時長） */
  seams: { v: number; from: number; to: number }[];
}

export function linearAxis(dayStart: number, dayLen: number): Axis {
  return { dayStart, length: dayLen, collapsed: false, pieces: [{ from: dayStart, to: dayStart + dayLen, v: 0 }], seams: [] };
}

/** 事件前後各留多少秒算「有事件的時段」。 */
export const EVENT_WINDOW_BEFORE_S = 10;
export const EVENT_WINDOW_AFTER_S = 30;

/** 只留有事件的時段：每個事件 [t - 10 s, t + 30 s]（夾在當天、不超過現在），重疊的合併；其他折成細縫。 */
export function collapsedAxis(dayStart: number, dayLen: number, events: number[], now: number): Axis {
  const hi = Math.min(dayStart + dayLen, Math.max(dayStart, now));
  const wins = events
    .filter((t) => t >= dayStart && t < dayStart + dayLen)
    .map((t) => ({ from: Math.max(dayStart, t - EVENT_WINDOW_BEFORE_S), to: Math.min(hi, t + EVENT_WINDOW_AFTER_S) }))
    .filter((w) => w.to > w.from)
    .sort((a, b) => a.from - b.from);
  const merged: { from: number; to: number }[] = [];
  for (const w of wins) {
    const last = merged[merged.length - 1];
    if (last && w.from <= last.to) last.to = Math.max(last.to, w.to);
    else merged.push({ ...w });
  }
  const pieces: AxisPiece[] = [];
  const seams: Axis['seams'] = [];
  let v = 0;
  let cursor = dayStart;
  for (const w of merged) {
    if (w.from > cursor) seams.push({ v, from: cursor, to: w.from });
    pieces.push({ from: w.from, to: w.to, v });
    v += w.to - w.from;
    cursor = w.to;
  }
  if (merged.length && cursor < dayStart + dayLen) seams.push({ v, from: cursor, to: dayStart + dayLen });
  return { dayStart, length: v, collapsed: true, pieces, seams };
}

/** 真實 → 虛擬。折掉的時段落在縫上（前一段的結尾）；線性軸在當天以外照樣外插。 */
export function toVirt(ax: Axis, t: number): number {
  if (!ax.collapsed) return t - ax.dayStart;
  for (const p of ax.pieces) {
    if (t < p.from) return p.v;
    if (t < p.to) return p.v + (t - p.from);
  }
  return ax.length;
}

/** 虛擬 → 真實。 */
export function toReal(ax: Axis, v: number): number {
  if (!ax.collapsed) return ax.dayStart + v;
  if (!ax.pieces.length) return ax.dayStart;
  if (v <= 0) return ax.pieces[0].from + v;
  for (const p of ax.pieces) if (v < p.v + (p.to - p.from)) return p.from + (v - p.v);
  const last = ax.pieces[ax.pieces.length - 1];
  return last.to + (v - (last.v + (last.to - last.from)));
}

/** 真實區間 → 虛擬區間（折掉的部分沒有；可能拆成好幾段）。 */
export function virtRanges(ax: Axis, a: number, b: number): [number, number][] {
  if (!ax.collapsed) return b > a ? [[a - ax.dayStart, b - ax.dayStart]] : [];
  const out: [number, number][] = [];
  for (const p of ax.pieces) {
    const s = Math.max(a, p.from);
    const e = Math.min(b, p.to);
    if (e > s) out.push([p.v + (s - p.from), p.v + (e - p.from)]);
  }
  return out;
}

/** 中線（虛擬秒）的範圍：[0, min(軸長, 現在)]。 */
export function virtBounds(ax: Axis, now: number): TimelineBounds {
  const nowV = ax.collapsed ? ax.length : Math.max(0, now - ax.dayStart);
  return { dayStart: 0, dayEnd: ax.length, now: nowV };
}

/** 播放時跳過折掉的時段：t 不在任何一段裡 → 下一段的開頭；後面沒有了 → null。 */
export function skipCollapsed(ax: Axis, t: number): number | null {
  if (!ax.collapsed) return t;
  for (const p of ax.pieces) {
    if (t < p.from) return p.from;
    if (t < p.to) return t;
  }
  return null;
}

export interface TimelineTick {
  /** 虛擬秒 */
  v: number;
  /** 真實時間 */
  t: number;
  major: boolean;
}

/** 可視範圍內的刻度（真實時間 dayStart + k × minor，對齊當天 00:00；折掉的時段沒有刻度）。 */
export function axisTicks(ax: Axis, center: number, span: number): TimelineTick[] {
  const { minor, major } = tickSteps(span);
  const v0 = center - span / 2;
  const v1 = center + span / 2;
  const ranges: [number, number][] = ax.collapsed
    ? ax.pieces.map((p) => [Math.max(p.from, toReal(ax, v0)), Math.min(p.to, toReal(ax, v1))] as [number, number]).filter(([a, b]) => b >= a)
    : [[toReal(ax, v0), toReal(ax, v1)]];
  const out: TimelineTick[] = [];
  for (const [a, b] of ranges) {
    for (let k = Math.ceil((a - ax.dayStart) / minor); ax.dayStart + k * minor <= b + 1e-6; k++) {
      const t = ax.dayStart + k * minor;
      out.push({ v: toVirt(ax, t), t, major: (((t - ax.dayStart) % major) + major) % major === 0 });
    }
  }
  return out;
}

/** 本地時區的時間軸用到的格式：秒數 < 60 的級距標籤帶秒。 */
export function tickWithSeconds(span: number): boolean {
  return tickSteps(span).major < 60;
}

// ---- 倍速（PLAY-04：雲端 0.25×–16×、本地回放隧道最多 4×） ----

export const SPEEDS = [0.25, 0.5, 1, 2, 4, 8, 16] as const;
export const LOCAL_MAX_SPEED = 4;

export function speedOptions(source: 'cloud' | 'local'): number[] {
  return SPEEDS.filter((s) => source === 'cloud' || s <= LOCAL_MAX_SPEED);
}

/** 換來源 / 換日期保留目前倍速，超過新來源上限就降到上限。 */
export function clampSpeed(current: number, source: 'cloud' | 'local'): number {
  return source === 'local' ? Math.min(current, LOCAL_MAX_SPEED) : current;
}
